import { ExternalLink } from 'lucide-react'
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox'
import { Field, Input, Select } from '@/components/ui/Field'
import { Detail, DetailLink } from '@/components/review/DetailList'
import type { Program, ProgramDetail, Segment, WbProgramTarget } from '@/types'
import { levelLabel, refLabel } from './columns'

/**
 * The program fields shared by the drawer and the review queue (APP-140):
 * the ones the team edits, and what the crawl extracted, shown as found.
 */

const DELIVERY_LABELS: Record<string, string> = {
  on_campus: 'On campus',
  online: 'Online',
  hybrid: 'Hybrid',
}

/**
 * The WB program a crawled program sits under (PC-33): pick one, name a new
 * one, or none. It assigns at once, outside the draft: it is its own request.
 */
export function WbProgramAssignField({
  value,
  options,
  onAssign,
}: {
  value: string | null
  options: ComboboxOption[]
  onAssign: (target: WbProgramTarget) => void
}) {
  return (
    <Field
      label="WB program"
      hint="The program the team publishes. Its name, segment and slate mapping are set on the WB program."
    >
      {(p) => (
        <Combobox
          {...p}
          value={value}
          options={options}
          onChange={(id) => onAssign(id ? { wbProgramId: id } : null)}
          onCreate={(name) => onAssign({ name })}
          noneLabel="Unassigned"
          searchLabel="Search or name a WB program"
        />
      )}
    </Field>
  )
}

export function SegmentField({
  value,
  segments,
  onChange,
}: {
  value: string | null
  segments: Segment[]
  onChange: (next: string | null) => void
}) {
  return (
    <Field label="Segment">
      {(p) => (
        <Select {...p} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Unassigned</option>
          {segments.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      )}
    </Field>
  )
}

/** Blank, or an http(s) URL: the API refuses anything else (`programUrl`). */
export const isValidProgramUrl = (value: string) => !value.trim() || /^https?:\/\/\S+$/i.test(value.trim())

/** Editable, so it is not repeated in `ProgramDetails` (APP-144). */
export function ProgramPageField({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const url = value.trim()
  return (
    <Field
      label="Program page"
      hint="The program's own page. Without one, a crawled program is listed under Other programs and left out of the translations table; one added by hand stays."
      error={isValidProgramUrl(value) ? undefined : 'Enter a full URL starting with http:// or https://.'}
    >
      {(p) => (
        <div className="flex items-center gap-1.5">
          <Input {...p} type="url" value={value} placeholder="https://" onChange={(e) => onChange(e.target.value)} />
          {url && isValidProgramUrl(url) && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              aria-label="Open the program page in a new tab"
              title="Open in a new tab"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] text-ink-muted transition-colors hover:bg-surface-sunk hover:text-brand-blue"
            >
              <ExternalLink size={15} aria-hidden />
            </a>
          )}
        </div>
      )}
    </Field>
  )
}

/**
 * `detail` is null until `useProgram` loads; the list row's fields show meanwhile.
 * `levelEditable`: the drawer edits the level (PC-25), so it is not repeated here.
 */
export function ProgramDetails({
  program,
  detail,
  levelEditable = false,
}: {
  program: Program
  detail: ProgramDetail | null | undefined
  levelEditable?: boolean
}) {
  return (
    <dl className="space-y-2 border-t border-rule pt-4">
      <Detail label="As crawled">{program.name}</Detail>
      <Detail label="Degree">{program.degree}</Detail>
      {!levelEditable && <Detail label="Level">{levelLabel(program.level)}</Detail>}
      <Detail label="Department">{program.department}</Detail>
      {program.mergedInto && <Detail label="Merged into">{program.mergedInto.name}</Detail>}
      <Detail label="Description">{program.description}</Detail>
      <Detail label="Length">{detail?.credentialLength}</Detail>
      <Detail label="Delivery">
        {detail && detail.deliveryModes.length > 0
          ? detail.deliveryModes.map((m) => DELIVERY_LABELS[m] ?? m).join(', ')
          : null}
      </Detail>
      <Detail label="Concentrations">
        {detail && detail.concentrations.length > 0 ? detail.concentrations.join(', ') : null}
      </Detail>
      <Detail label="CIP code">{detail?.cipCode}</Detail>
      {detail?.notes && <Detail label="Notes">{detail.notes}</Detail>}
      <Detail label="Found on">
        {detail && detail.pages.length > 0 ? (
          <ul className="space-y-1">
            {detail.pages.map((page) => (
              <li key={page.id}>
                <DetailLink url={page.url} />
              </li>
            ))}
          </ul>
        ) : program.sourceUrl ? (
          <DetailLink url={program.sourceUrl} />
        ) : null}
      </Detail>
      {detail && detail.merged.length > 0 && (
        <Detail label="Merged here">
          <ul className="space-y-1">
            {detail.merged.map((m) => (
              <li key={m.id}>{refLabel(m)}</li>
            ))}
          </ul>
        </Detail>
      )}
    </dl>
  )
}
